import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, X, Monitor, LogIn, Star, PanelLeftClose } from 'lucide-react';
import {
  apiClient,
  type PlatformBranding,
  type PlatformAssetKind,
} from '@/lib/api-client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PREDEFINED_COLORS = [
  '#FFFFFF',
  '#F8FAFC',
  '#1E293B',
  '#0F172A',
  '#7C3AED',
  '#2563EB',
  '#0891B2',
  '#059669',
  '#D97706',
  '#DC2626',
];

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Erro inesperado';
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

interface SectionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function SectionCard({ title, description, icon, children }: SectionCardProps) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-300">
          {icon}
        </div>
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">{title}</h2>
          <p className="mt-0.5 text-xs text-neutral-400">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

interface AssetUploaderProps {
  label: string;
  accept: string;
  maxBytes: number;
  currentUrl: string | null;
  onUpload(file: File): void;
  onRemove(): void;
  isUploading: boolean;
  isRemoving: boolean;
  previewSize?: number;
  hint?: string;
}

function AssetUploader({
  label,
  accept,
  maxBytes,
  currentUrl,
  onUpload,
  onRemove,
  isUploading,
  isRemoving,
  previewSize = 48,
  hint,
}: AssetUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLocalError(null);
    if (file.size > maxBytes) {
      setLocalError(`Arquivo muito grande (max ${Math.round(maxBytes / 1024 / 1024)} MB)`);
      return;
    }
    onUpload(file);
    // Reset para permitir re-upload do mesmo arquivo
    e.target.value = '';
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        {currentUrl ? (
          <div className="flex shrink-0 items-center justify-center rounded-md border border-neutral-700 bg-neutral-800 p-2">
            <img
              src={currentUrl}
              alt={label}
              style={{ height: previewSize, maxWidth: 160, objectFit: 'contain' }}
            />
          </div>
        ) : (
          <div
            className="flex shrink-0 items-center justify-center rounded-md border border-dashed border-neutral-700 bg-neutral-800/50 text-neutral-500"
            style={{ height: previewSize + 16, width: previewSize + 16 }}
          >
            <Upload className="h-5 w-5" />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={isUploading || isRemoving}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Upload className="h-3.5 w-3.5" />
            {isUploading ? 'Enviando...' : 'Enviar imagem'}
          </button>
          {currentUrl ? (
            <button
              type="button"
              disabled={isUploading || isRemoving}
              onClick={onRemove}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-900/60 bg-red-950/40 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-950/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              {isRemoving ? 'Removendo...' : 'Remover'}
            </button>
          ) : null}
        </div>
      </div>
      {hint ? <p className="text-xs text-neutral-500">{hint}</p> : null}
      {localError ? (
        <p className="text-xs text-red-400">{localError}</p>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}

interface SliderFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange(v: number): void;
  unit?: string;
}

function SliderField({ label, value, min, max, onChange, unit = 'px' }: SliderFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-neutral-300">{label}</label>
        <span className="text-xs tabular-nums text-neutral-400">
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-neutral-700 accent-violet-500"
      />
      <div className="flex justify-between text-[10px] text-neutral-600">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bloco 1 — Identidade visual (header)
// ---------------------------------------------------------------------------

interface HeaderIdentityProps {
  branding: PlatformBranding;
  onRefresh(): void;
}

function HeaderIdentityBlock({ branding, onRefresh }: HeaderIdentityProps) {
  const qc = useQueryClient();

  // Estado local de edição
  const [logoSize, setLogoSize] = useState(branding.headerLogoSizePx);
  const [headerName, setHeaderName] = useState(branding.headerName ?? '');
  const [textColor, setTextColor] = useState(branding.headerTextColor);
  const [hexInput, setHexInput] = useState(branding.headerTextColor);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'platform-settings'] });
    onRefresh();
  };

  const uploadMut = useMutation({
    mutationFn: (file: File) => apiClient.adminUploadPlatformAsset('header-logo', file),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  const removeMut = useMutation({
    mutationFn: () => apiClient.adminRemovePlatformAsset('header-logo'),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      apiClient.adminPatchPlatformSettings({
        headerLogoSizePx: logoSize,
        headerName: headerName.trim() || null,
        headerTextColor: textColor,
      }),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  function handleColorPick(hex: string) {
    setTextColor(hex);
    setHexInput(hex);
  }

  function handleHexChange(raw: string) {
    setHexInput(raw);
    if (/^#[0-9A-Fa-f]{6}$/.test(raw)) {
      setTextColor(raw);
    }
  }

  // Preview
  const previewBg = '#1E293B';

  return (
    <SectionCard
      title="Identidade visual (cabeçalho)"
      description="Logo e nome exibidos na barra superior de todos os escritórios"
      icon={<Monitor className="h-4 w-4" />}
    >
      <div className="space-y-5">
        <AssetUploader
          label="Logo do cabeçalho"
          accept="image/jpeg,image/png,image/svg+xml,image/gif,image/webp"
          maxBytes={5 * 1024 * 1024}
          currentUrl={branding.headerLogoUrl}
          onUpload={(f) => uploadMut.mutate(f)}
          onRemove={() => removeMut.mutate()}
          isUploading={uploadMut.isPending}
          isRemoving={removeMut.isPending}
          previewSize={logoSize}
          hint="JPG, PNG, SVG ou GIF. Máximo 5 MB."
        />

        <SliderField
          label="Tamanho do logo"
          value={logoSize}
          min={24}
          max={120}
          onChange={setLogoSize}
        />

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-neutral-300">
            Nome no cabeçalho
          </label>
          <input
            type="text"
            maxLength={100}
            placeholder="Deixe vazio para ocultar"
            value={headerName}
            onChange={(e) => setHeaderName(e.target.value)}
            className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-violet-500 focus:outline-none"
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-neutral-300">Cor do texto</label>
          <div className="flex flex-wrap gap-2">
            {PREDEFINED_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                title={c}
                onClick={() => handleColorPick(c)}
                className={`h-7 w-7 rounded-md border-2 transition-transform hover:scale-110 ${
                  textColor === c
                    ? 'border-violet-400 scale-110'
                    : 'border-neutral-700'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={textColor}
              onChange={(e) => handleColorPick(e.target.value)}
              className="h-7 w-10 cursor-pointer rounded border border-neutral-700 bg-neutral-800 p-0.5"
            />
            <input
              type="text"
              value={hexInput}
              maxLength={7}
              onChange={(e) => handleHexChange(e.target.value)}
              placeholder="#FFFFFF"
              className="w-28 rounded-md border border-neutral-700 bg-neutral-800 px-2 py-1 font-mono text-xs text-neutral-100 focus:border-violet-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Preview */}
        <div>
          <p className="mb-2 text-xs font-medium text-neutral-400">Pré-visualização</p>
          <div
            className="flex h-12 items-center gap-3 rounded-md px-4"
            style={{ backgroundColor: previewBg }}
          >
            {branding.headerLogoUrl ? (
              <img
                src={branding.headerLogoUrl}
                alt="Logo"
                style={{ height: logoSize, maxWidth: 120, objectFit: 'contain' }}
              />
            ) : (
              <div
                className="flex shrink-0 items-center justify-center rounded bg-neutral-700"
                style={{ height: logoSize, width: logoSize }}
              >
                <span className="text-[10px] text-neutral-400">Logo</span>
              </div>
            )}
            {headerName.trim() ? (
              <span
                className="text-sm font-semibold"
                style={{ color: textColor }}
              >
                {headerName}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveMut.isPending ? 'Salvando...' : 'Salvar aparência'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Bloco 2 — Logo da tela de login
// ---------------------------------------------------------------------------

interface LoginLogoProps {
  branding: PlatformBranding;
  onRefresh(): void;
}

function LoginLogoBlock({ branding, onRefresh }: LoginLogoProps) {
  const qc = useQueryClient();
  const [logoSize, setLogoSize] = useState(branding.loginLogoSizePx);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'platform-settings'] });
    onRefresh();
  };

  const uploadMut = useMutation({
    mutationFn: (file: File) => apiClient.adminUploadPlatformAsset('login-logo', file),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  const removeMut = useMutation({
    mutationFn: () => apiClient.adminRemovePlatformAsset('login-logo'),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  const saveSizeMut = useMutation({
    mutationFn: () => apiClient.adminPatchPlatformSettings({ loginLogoSizePx: logoSize }),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  return (
    <SectionCard
      title="Logo da tela de login"
      description="Imagem exibida acima do formulário de acesso"
      icon={<LogIn className="h-4 w-4" />}
    >
      <div className="space-y-5">
        <AssetUploader
          label="Logo do login"
          accept="image/jpeg,image/png,image/svg+xml,image/gif,image/webp"
          maxBytes={10 * 1024 * 1024}
          currentUrl={branding.loginLogoUrl}
          onUpload={(f) => uploadMut.mutate(f)}
          onRemove={() => removeMut.mutate()}
          isUploading={uploadMut.isPending}
          isRemoving={removeMut.isPending}
          previewSize={logoSize}
          hint="JPG, PNG, SVG ou GIF. Máximo 10 MB."
        />

        <SliderField
          label="Tamanho do logo"
          value={logoSize}
          min={60}
          max={240}
          onChange={setLogoSize}
        />

        {/* Preview mock de tela de login */}
        <div>
          <p className="mb-2 text-xs font-medium text-neutral-400">Pré-visualização</p>
          <div className="flex flex-col items-center rounded-md border border-neutral-800 bg-neutral-950 py-8 px-6">
            {branding.loginLogoUrl ? (
              <img
                src={branding.loginLogoUrl}
                alt="Logo login"
                style={{ height: logoSize, maxWidth: 280, objectFit: 'contain' }}
                className="mb-6"
              />
            ) : (
              <div
                className="mb-6 flex items-center justify-center rounded-md border border-dashed border-neutral-700 bg-neutral-800 text-neutral-500"
                style={{ height: logoSize, width: logoSize }}
              >
                <LogIn className="h-6 w-6" />
              </div>
            )}
            <div className="w-full max-w-xs space-y-3">
              <div className="h-9 rounded-md bg-neutral-800" />
              <div className="h-9 rounded-md bg-neutral-800" />
              <div className="h-9 rounded-md bg-violet-700" />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saveSizeMut.isPending}
            onClick={() => saveSizeMut.mutate()}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveSizeMut.isPending ? 'Salvando...' : 'Salvar tamanho'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Bloco 3 — Favicon
// ---------------------------------------------------------------------------

interface FaviconProps {
  branding: PlatformBranding;
  onRefresh(): void;
}

function FaviconBlock({ branding, onRefresh }: FaviconProps) {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'platform-settings'] });
    onRefresh();
  };

  const uploadMut = useMutation({
    mutationFn: (file: File) => apiClient.adminUploadPlatformAsset('favicon', file),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  const removeMut = useMutation({
    mutationFn: () => apiClient.adminRemovePlatformAsset('favicon'),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  return (
    <SectionCard
      title="Favicon"
      description="Ícone exibido na aba do navegador"
      icon={<Star className="h-4 w-4" />}
    >
      <div className="space-y-5">
        <AssetUploader
          label="Favicon"
          accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
          maxBytes={1 * 1024 * 1024}
          currentUrl={branding.faviconUrl}
          onUpload={(f) => uploadMut.mutate(f)}
          onRemove={() => removeMut.mutate()}
          isUploading={uploadMut.isPending}
          isRemoving={removeMut.isPending}
          previewSize={32}
          hint="PNG, ICO ou SVG. Recomendado 32x32 ou 64x64. Máximo 1 MB."
        />

        {/* Preview de aba do navegador */}
        <div>
          <p className="mb-2 text-xs font-medium text-neutral-400">Pré-visualização (aba)</p>
          <div className="inline-flex items-center gap-2 rounded-t-md border border-b-0 border-neutral-700 bg-neutral-900 px-3 py-1.5">
            {branding.faviconUrl ? (
              <img src={branding.faviconUrl} alt="Favicon" className="h-4 w-4" />
            ) : (
              <div className="h-4 w-4 rounded-sm bg-neutral-700" />
            )}
            <span className="text-xs text-neutral-300">VoxisAdv</span>
            <X className="h-3 w-3 text-neutral-600" />
          </div>
          <div className="h-0.5 w-full bg-neutral-700" />
        </div>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Bloco 4 — Logo da sidebar minimizada
// ---------------------------------------------------------------------------

interface CollapsedLogoProps {
  branding: PlatformBranding;
  onRefresh(): void;
}

function CollapsedLogoBlock({ branding, onRefresh }: CollapsedLogoProps) {
  const qc = useQueryClient();
  const [logoSize, setLogoSize] = useState(branding.collapsedLogoSizePx ?? 32);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'platform-settings'] });
    onRefresh();
  };

  const uploadMut = useMutation({
    mutationFn: (file: File) => apiClient.adminUploadPlatformAsset('collapsed-logo', file),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  const removeMut = useMutation({
    mutationFn: () => apiClient.adminRemovePlatformAsset('collapsed-logo'),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  const saveSizeMut = useMutation({
    mutationFn: () => apiClient.adminPatchPlatformSettings({ collapsedLogoSizePx: logoSize }),
    onSuccess: invalidate,
    onError: (e) => window.alert(getErrorMessage(e)),
  });

  return (
    <SectionCard
      title="Logo da sidebar minimizada"
      description="Ícone quadrado exibido no lugar do botão de toggle quando o menu está recolhido"
      icon={<PanelLeftClose className="h-4 w-4" />}
    >
      <div className="space-y-5">
        <AssetUploader
          label="Logo sidebar minimizada"
          accept="image/jpeg,image/png,image/svg+xml,image/gif"
          maxBytes={2 * 1024 * 1024}
          currentUrl={branding.collapsedLogoUrl}
          onUpload={(f) => uploadMut.mutate(f)}
          onRemove={() => removeMut.mutate()}
          isUploading={uploadMut.isPending}
          isRemoving={removeMut.isPending}
          previewSize={logoSize}
          hint="JPG, PNG, SVG ou GIF. Recomendado imagem quadrada. Máximo 2 MB."
        />

        <SliderField
          label="Tamanho do icone"
          value={logoSize}
          min={20}
          max={48}
          onChange={setLogoSize}
        />

        {/* Preview mock de sidebar minimizada */}
        <div>
          <p className="mb-2 text-xs font-medium text-neutral-400">Pré-visualização (sidebar recolhida)</p>
          <div className="flex">
            <div className="flex w-16 flex-col items-center gap-3 rounded-l-md border border-neutral-700 bg-neutral-950 py-4">
              {branding.collapsedLogoUrl ? (
                <img
                  src={branding.collapsedLogoUrl}
                  alt="Logo minimizada"
                  style={{ width: logoSize, height: logoSize, objectFit: 'contain' }}
                  className="rounded"
                />
              ) : (
                <div
                  className="flex items-center justify-center rounded-md border border-dashed border-neutral-700 bg-neutral-800 text-neutral-500"
                  style={{ width: logoSize, height: logoSize }}
                >
                  <PanelLeftClose className="h-3 w-3" />
                </div>
              )}
              <div className="h-2 w-8 rounded-sm bg-neutral-800" />
              <div className="h-2 w-8 rounded-sm bg-neutral-800" />
              <div className="h-2 w-8 rounded-sm bg-neutral-800" />
            </div>
            {/* Botao de toggle fora do sidebar */}
            <div className="flex items-start pt-4">
              <div className="flex h-7 w-4 items-center justify-center rounded-r-md border border-l-0 border-neutral-700 bg-neutral-900 text-neutral-500">
                <span className="text-[10px]">›</span>
              </div>
            </div>
          </div>
          <p className="mt-2 text-[10px] text-neutral-600">
            A aba de toggle fica fixada na borda direita do sidebar recolhido.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saveSizeMut.isPending}
            onClick={() => saveSizeMut.mutate()}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveSizeMut.isPending ? 'Salvando...' : 'Salvar tamanho'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function AdminAparenciaPage() {
  const { data: branding, isPending, isError } = useQuery({
    queryKey: ['admin', 'platform-settings'],
    queryFn: () => apiClient.adminGetPlatformSettings(),
  });

  function handleRefresh() {
    // Vazio — invalidateQueries no bloco filho ja cuida disso
  }

  if (isPending) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-48 animate-pulse rounded-lg border border-neutral-800 bg-neutral-900"
          />
        ))}
      </div>
    );
  }

  if (isError || !branding) {
    return (
      <div className="rounded-lg border border-red-900/50 bg-red-950/30 p-4 text-sm text-red-200">
        Não foi possível carregar as configurações de aparência.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-100">
          Aparência da plataforma
        </h1>
        <p className="mt-1 text-sm text-neutral-400">
          Configurações visuais globais aplicadas a todos os escritórios.
        </p>
      </header>

      <HeaderIdentityBlock branding={branding} onRefresh={handleRefresh} />
      <LoginLogoBlock branding={branding} onRefresh={handleRefresh} />
      <FaviconBlock branding={branding} onRefresh={handleRefresh} />
      <CollapsedLogoBlock branding={branding} onRefresh={handleRefresh} />
    </div>
  );
}
